import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { UPDATE_TRANSPOREON_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type TCustomer = {
  customerNameInChaine: string
  customerSequenceNumber?: number
  isNewCustomer?: boolean
}

const formSchema = z.object({
  customerNames: z.record(
    z.object({
      customerNameInChaine: z.string().min(1, 'Chain Customer Name is required'),
      customerSequenceNumber: z.number().optional(),
      isNewCustomer: z.boolean().optional()
    })
  ),
  password: z.string().min(1, 'Password is required'),
  username: z.string().min(1, 'Username is required')
})

type UpdateTransporeonAppInput = {
  appType: AppTypeEnum
  password: string
  updates: { created: TCustomer[]; deleted: TCustomer[] }
  username: string
}

/**
 * This is the modal for the TransporeonUpdateModal app.
 */
export function TransporeonUpdateModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const [formError, setFormError] = useState('')
  const existingCustomers = app?.workspaceApp?.appMetadata?.customers || []
  const existingUsername = app?.workspaceApp?.appCredentials?.username || ''
  const existingPassword = app?.workspaceApp?.appCredentials?.password || ''

  const deletedCustomersRef = useRef<TCustomer[]>([])
  const createdCustomersRef = useRef<TCustomer[]>([])

  // Function to generate initial customers data
  const getInitialCustomers = () => {
    return existingCustomers.length > 0
      ? existingCustomers.reduce(
          (acc, customer) => {
            acc[customer.customerSequenceNumber || 0] = {
              customerNameInChaine: customer.customerNameInChaine || '',
              customerSequenceNumber: customer.customerSequenceNumber || undefined,
              isNewCustomer: customer.isNewCustomer || false
            }
            return acc
          },
          {} as Record<number, TCustomer>
        )
      : {
          0: {
            customerNameInChaine: '',
            customerSequenceNumber: undefined,
            isNewCustomer: false
          }
        }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      customerNames: getInitialCustomers(),
      password: existingPassword,
      username: existingUsername
    },
    resolver: zodResolver(formSchema)
  })

  // Reset form when app data changes
  useEffect(() => {
    if (app) {
      const initialCustomers = getInitialCustomers()
      form.reset({
        customerNames: initialCustomers,
        password: existingPassword,
        username: existingUsername
      })
      // Reset refs when form resets
      deletedCustomersRef.current = []
      createdCustomersRef.current = []
    }
  }, [app, existingCustomers, existingPassword, existingUsername, form])

  const [updateTransporeonApp, { loading }] = useMutation<
    {
      updateCustomersInTransporeon: {
        appType: AppTypeEnum.Transporeon
        password: string
        updates: { created: TCustomer[]; deleted: TCustomer[] }
        username: string
      }
    },
    UpdateTransporeonAppInput
  >(UPDATE_TRANSPOREON_APP, {
    onCompleted: () => {
      toast.success('Transporeon app is updated successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Incorrect username or password'
          ? 'Invalid username or password. Please try again.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    deletedCustomersRef.current = []
    createdCustomersRef.current = []
    onClose()
  }

  const addCustomerName = () => {
    const currentCustomerNames = form.getValues('customerNames')
    const maxCustomerSequenceNumber = Math.max(...Object.keys(currentCustomerNames).map(Number))
    form.setValue('customerNames', {
      ...currentCustomerNames,
      [maxCustomerSequenceNumber + 1]: {
        customerNameInChaine: '',
        customerSequenceNumber: maxCustomerSequenceNumber ? maxCustomerSequenceNumber + 1 : 1,
        isNewCustomer: true
      }
    })
  }

  const removeCustomerName = (customerSequenceNumber: number | undefined) => {
    const currentCustomerNames = form.getValues('customerNames')
    if (Object.keys(currentCustomerNames).length > 1) {
      const customerToRemove = currentCustomerNames[customerSequenceNumber || 0]
      delete customerToRemove.isNewCustomer
      if (!customerToRemove?.isNewCustomer) {
        deletedCustomersRef.current = [...deletedCustomersRef.current, customerToRemove]
        delete currentCustomerNames[customerSequenceNumber || 0]
      }
      form.setValue(
        'customerNames',
        Object.fromEntries(
          Object.entries(currentCustomerNames).filter(([key]) => key !== customerSequenceNumber?.toString())
        )
      )
    }
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const newCustomers = Object.values(values.customerNames).filter((customer) => customer.isNewCustomer)

    if (newCustomers.length > 0 || deletedCustomersRef.current.length > 0) {
      updateTransporeonApp({
        variables: {
          appType: appType,
          password: values.password,
          updates: {
            created: newCustomers?.map((customer) => ({
              customerNameInChaine: customer.customerNameInChaine
            })),
            deleted: deletedCustomersRef.current
          },
          username: values.username
        }
      })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Update Transporeon for Chain</DialogTitle>
          <DialogDescription>Update your Transporeon integration settings and customer IDs.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              form.handleSubmit(onSubmit)(e)
            }}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter username" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter password" {...field} disabled={loading} type="password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {Object.values(form.watch('customerNames')).map((customerKey, index) => (
              <div
                key={`customer-${customerKey.customerSequenceNumber}`}
                className="space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Customer {index + 1}</h4>
                  {Object.keys(form.watch('customerNames')).length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCustomerName(customerKey.customerSequenceNumber)}
                      disabled={loading}
                    >
                      Remove
                    </Button>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name={`customerNames.${customerKey.customerSequenceNumber}.customerNameInChaine`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Chain Customer Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter Chain Customer Name"
                          {...field}
                          disabled={!customerKey.isNewCustomer || loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addCustomerName} disabled={loading}>
              Add Another Customer
            </Button>

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href="https://handbook.chaineapp.com/product-guide/apps/transporeon"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
